import Icon from "@mdi/react";
import { mdiAccountGroup, mdiPlus } from "@mdi/js";
import { Link } from "easy-page-router/react";

import { Footer, Header } from "../../Layout";
import { useHead } from "../../services/common";
import { ContentSwitch, Loading, OtherPolls, Page } from "../../components";
import { ThemeDiv, ThemeSwitch } from "../../components/Theme";
import { LangSwitch } from "../../components/Lang";
import { useLang } from "../../services/lang";
import { useLocalHistory } from "../../services/localStorage";
import { useApi, useHandleError } from "../../services/api";

import { WhoPreview } from "../../../types";
import { useEffect, useState } from "react";
import { useSSRHook } from "ssr-hook";
import LaunchButton from "../../components/LaunchButton";


export default function WhoBoard() {
    const { dict } = useLang();

    useHead({
        title: dict.seo.whoBoard.title,
        description: dict.seo.whoBoard.description,
    });

    const [contentIndex, setContentIndex] = useState(1);

    return <Page fixed={<>
        <Header title={dict.who} link={`/${dict.urlWho}`}>
            <LangSwitch />
            <ThemeSwitch />
        </Header>
        <Footer />
    </>}>

        <h1>{dict.who} <span className="sub-title">{dict.whoSubTitle}</span></h1>

        <p className="page-description">{dict.seo.whoBoard.text}</p>

        <Link className="button-new" href={`/${dict.urlWho}/${dict.urlNew}`}>
            <Icon path={mdiPlus} size={1} /> {dict.newWho} <Icon path={mdiAccountGroup} size={1} />
        </Link>

        <OtherPolls what when />

        <ContentSwitch
            index={contentIndex}
            onChange={setContentIndex}
            options={[{
                title: dict.pollsLocal,
                content: <Local onLoad={() => setContentIndex(0)} />,
            }, {
                title: dict.pollsPublic,
                content: <Public />,
            }]}
        />

    </Page>;
}

function Local({ onLoad }: { onLoad: () => void }) {
    const whoIds = useLocalHistory("who");
    const [whos, error, isLoading, reload] = useApi.getWhoPreviews({ whoIds });
    useHandleError(error);

    useEffect(() => {
        if (whos && whos.length > 0) onLoad();
    }, [whos]);

    return <Show whos={whos} isLoading={isLoading} error={error} />;
}

function Public() {
    const { lang } = useLang();

    const [whos, error, isLoading, reload] = useSSRHook<WhoPreview[]>(`/api/who-previews-public?lang=${lang}`);
    useHandleError(error);

    return <Show whos={whos} isLoading={isLoading} error={error} />;
}

type ShowProps = {
    whos: null | WhoPreview[];
    error: null | string | Error;
    isLoading: boolean;
}

function Show({ whos, isLoading, error }: ShowProps) {
    const { dict } = useLang();

    return <>
        {isLoading && <Loading />}
        {error && <p className="error">{dict.whosError}</p>}
        {whos?.length === 0 && <p className="info">{dict.noWhos}</p>}
        {whos && <div className="grid whos">
            {whos.map(w => <WhoPreviewElement key={w._id} who={w} className="grid-item" />)}
        </div>}
    </>
}

function WhoPreviewElement({ who, className }: { who: WhoPreview, className: string }) {
    const { dict } = useLang();

    return <Link href={`/${dict.urlWho}/${who._id}`}>
        <ThemeDiv theme={who.theme} className={`who-preview preview theme-${who.theme} ${className}`}>
            <h3 className="line-clamp-2">{who.title}</h3>
            <p className="pre-line line-clamp-3">{who.description}</p>
            <div className="who-votes">
                <div className="who-vote vote-yes active">{who.votes.yes}x</div>
                {who.isMaybeOption && <div className="who-vote vote-maybe active">{who.votes.maybe}x</div>}
                <div className="who-vote vote-no active">{who.votes.no}x</div>
            </div>
        </ThemeDiv>
    </Link>;
}